import { DobPipe } from '../../pipes/dob.pipe';
import { MedBadgesComponent } from '../../med-badges/med-badges.component';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BatchPickService } from '../batchpick.service';
import { BasketDetail, BatchPickState, MedicationDetail } from '../models/batchpick-state';
import { IssueBannerComponent, IssueBannerInput } from './issue-banner.component';

@Component({
  selector: 'app-batchpick-verify-basket',
  standalone: true,
  imports: [DobPipe, CommonModule, FormsModule, IssueBannerComponent, MedBadgesComponent],
  templateUrl: './verify-basket.component.html',
  styleUrl: './verify-basket.component.css',
})
export class VerifyBasketComponent {
  @ViewChild('scanInput') scanInput!: ElementRef<HTMLInputElement>;
  batchId!: string;
  basketNumber!: number;
  state?: BatchPickState;
  basket?: BasketDetail;
  barcode = '';
  issue: IssueBannerInput | null = null;
  banner: { type: 'success' | 'warn' | 'error'; text: string } | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: BatchPickService,
  ) {}

  ngOnInit() {
    this.batchId = this.route.snapshot.paramMap.get('batchId')!;
    this.basketNumber = Number(this.route.snapshot.paramMap.get('n'));
    this.refresh();
  }

  refresh() {
    this.api.state(this.batchId).subscribe(s => {
      this.state = s;
      this.basket = s.baskets.find(b => b.basketNumber === this.basketNumber);
      queueMicrotask(() => this.scanInput?.nativeElement.focus());
    });
  }

  scan() {
    const code = this.barcode.trim();
    if (!code) return;
    this.barcode = '';
    this.api.verifyScan(this.batchId, this.basketNumber, code).subscribe({
      next: r => {
        if (r.kind === 'Verified') {
          this.issue = null;
          this.banner = { type: 'success', text: `Verified${r.isSubstitute ? ' (ALT)' : ''} — ${r.basketFill}` };
          this.refresh();
        } else if (r.kind === 'AlreadyVerified') {
          this.issue = null;
          this.banner = { type: 'warn', text: 'Already verified.' };
        } else if (r.kind === 'WrongBasket') {
          const targetPatient = this.state?.baskets.find(b => b.basketNumber === r.belongsInBasket)?.patientName;
          this.issue = {
            kind: 'WrongBasket',
            medicationId: r.medicationId,
            medicationName: this.findMedName(r.medicationId),
            scannedProductId: code,
            expectedBasket: r.belongsInBasket,
            expectedPatient: targetPatient,
          };
          this.banner = null;
        } else if (r.kind === 'OrphanStock') {
          this.issue = { kind: 'OrphanStock', scannedProductId: code };
          this.banner = null;
        }
        queueMicrotask(() => this.scanInput?.nativeElement.focus());
      },
      error: () => { this.banner = { type: 'error', text: 'Scan failed — retry.' }; },
    });
  }

  private findMedName(medId?: string): string | undefined {
    if (!medId || !this.state) return undefined;
    for (const b of this.state.baskets) {
      const m = b.medications.find(x => x.medicationId === medId);
      if (m) return m.medicationName;
    }
    return undefined;
  }

  moveNow() {
    if (!this.issue || this.issue.kind !== 'WrongBasket' || !this.issue.medicationId || this.issue.expectedBasket === undefined) return;
    const target = this.issue.expectedBasket;
    this.api.moveNow(this.batchId, this.basketNumber, this.issue.medicationId, this.basketNumber, target).subscribe(() => {
      this.issue = null;
      this.banner = { type: 'success', text: `Moved to basket #${target}.` };
      this.refresh();
    });
  }

  defer() {
    if (!this.issue) return;
    this.api.deferIssue(this.batchId, {
      kind: this.issue.kind,
      medicationId: this.issue.medicationId,
      scannedProductId: this.issue.scannedProductId,
      scannedInBasket: this.issue.kind === 'WrongBasket' ? this.basketNumber : undefined,
      expectedBasket: this.issue.kind === 'WrongBasket' ? this.issue.expectedBasket : undefined,
    }).subscribe(() => {
      this.issue = null;
      this.banner = { type: 'warn', text: 'Deferred to end-of-session resolution.' };
      this.refresh();
    });
  }

  dismiss() { this.issue = null; }

  back() {
    this.router.navigate(['/batchpick', this.batchId, 'verify']);
  }

  rowState(m: MedicationDetail): string {
    if (m.pickScanStatus === 'Missing') return 'missing';
    if (m.verifyScanStatus === 'Verified') return 'verified';
    if (m.pickScanStatus === 'Picked' || m.pickScanStatus === 'PickedAsSubstitute') return 'picked';
    return 'notpicked';
  }
}
